import {
  LoadFileEncoding,
  ParseHeaderSampleResponse,
} from '../../../../../../../../../backend/src/integrations/relativity/load-file-import/load-file-import-types';
import { type LoadFileColumnMappingInput } from '@/api/mutations/useStartLoadFileImport';
import { type LoadFileColumnMapping, type LoadFileImportListItem } from '@/api/queries/useGetLoadFileImports';
import { generateFieldKey } from '@/new/pages/organisation-settings/components/field-mappings';

/** Relativity field-type strings the wizard offers (chosen so the backend infers the right stored type). */
export const FIELD_TYPE_OPTIONS = [
  'Fixed-Length Text',
  'Long Text',
  'Whole Number',
  'Decimal',
  'Date',
  'Yes/No',
  'Single Choice',
  'Multiple Choice',
] as const;

export const ENCODING_OPTIONS: { value: LoadFileEncoding; label: string }[] = [
  { value: 'utf-8', label: 'UTF-8' },
  { value: 'utf-16le', label: 'UTF-16 LE' },
  { value: 'utf-16be', label: 'UTF-16 BE' },
  { value: 'win1252', label: 'Windows-1252' },
];

/** One selectable delimiter character, labelled in Relativity's "glyph (Unicode name)" style. */
export interface DelimiterOption {
  value: string;
  label: string;
}

export const COLUMN_DELIMITER_OPTIONS: DelimiterOption[] = [
  { value: '\u0014', label: 'DC4 (Device control 4)' },
  { value: '\t', label: 'Tab (Horizontal tab)' },
  { value: ',', label: ', (Comma)' },
  { value: '|', label: '| (Pipe)' },
];

export const QUOTE_OPTIONS: DelimiterOption[] = [
  { value: 'þ', label: 'þ (Latin Small Letter Thorn)' },
  { value: '"', label: '" (Quotation mark)' },
];

export const NEWLINE_OPTIONS: DelimiterOption[] = [
  { value: '®', label: '® (Registered sign)' },
  { value: '\n', label: 'Newline (Line feed)' },
  { value: '¶', label: '¶ (Pilcrow sign)' },
];

export const MULTI_VALUE_OPTIONS: DelimiterOption[] = [
  { value: ';', label: '; (Semicolon)' },
  { value: ',', label: ', (Comma)' },
  { value: '|', label: '| (Pipe)' },
  { value: '#', label: '# (Number sign)' },
];

/**
 * Ensure the current (possibly auto-detected) value is always in the option
 * list so the Select never renders blank. An unknown character is appended
 * with a generic "glyph (U+XXXX)" label.
 */
export function withDetectedOption(options: DelimiterOption[], value: string | undefined): DelimiterOption[] {
  if (value === undefined || value.length === 0 || options.some((o) => o.value === value)) return options;
  const codePoint = (value.codePointAt(0) ?? 0).toString(16).toUpperCase().padStart(4, '0');
  return [...options, { value, label: `${value.trim() || 'Character'} (U+${codePoint})` }];
}

export const ACCEPTED_EXTENSIONS = '.dat,.txt,.csv';

export interface ColumnMappingState extends LoadFileColumnMappingInput {}

/** Values (case-insensitive) that mark a column as a Yes/No field. */
const YES_NO_VALUES = new Set(['yes', 'no', 'true', 'false', 'y', 'n']);

/**
 * Drop a file extension: everything from the last `.` onward, including the dot.
 * A leading dot (dotfile) or a value with no dot is left untouched so the
 * basename is never reduced to an empty string. Mirrors the backend helper.
 */
export function stripExtension(value: string): string {
  const lastDot = value.lastIndexOf('.');
  return lastDot > 0 ? value.slice(0, lastDot) : value;
}

/**
 * Mirror of the backend `normalizeForMatch` so the wizard can preview the match
 * key and compute match percentages. Matching is CASE-SENSITIVE (no lowercase),
 * matching the backend. Called once per side, so `stripExtension` is supplied per
 * side by the caller.
 */
export function normalizeForMatch(raw: string, options: { stripExtension: boolean }): string {
  let value = raw.trim();
  if (options.stripExtension) value = stripExtension(value);
  return value;
}

/** Conservatively decide whether a value reads as a date (needs date-ish separators). */
function isDateLike(value: string): boolean {
  if (!/^\d{1,4}[-/]\d{1,2}[-/]\d{1,4}([ T]\d{1,2}:\d{2}(:\d{2})?)?/.test(value)) return false;
  return !Number.isNaN(Date.parse(value));
}

/**
 * Best-effort field-type guess from a column's sampled values (no AI). Returns
 * the Relativity field-type string the wizard offers; the user can override it.
 * Only fires when *every* non-empty sample agrees, so a single odd row keeps the
 * safe text default.
 */
export function inferFieldType(sampleValues: string[]): (typeof FIELD_TYPE_OPTIONS)[number] {
  const values = sampleValues.map((v) => v.trim()).filter((v) => v.length > 0);
  if (values.length === 0) return 'Fixed-Length Text';

  if (values.every((v) => YES_NO_VALUES.has(v.toLowerCase()))) return 'Yes/No';
  if (values.every((v) => /^-?\d+$/.test(v))) return 'Whole Number';
  if (values.every((v) => /^-?(\d+\.\d*|\.\d+|\d+)$/.test(v)) && values.some((v) => v.includes('.'))) {
    return 'Decimal';
  }
  if (values.every(isDateLike)) return 'Date';
  if (values.some((v) => v.length > 255)) return 'Long Text';
  return 'Fixed-Length Text';
}

/**
 * Decide whether a column reads as a separator-delimited multi-value field.
 * Requires a majority of non-empty samples to contain the separator so a stray
 * occurrence in free text doesn't misfire. A multi-value column maps to
 * Long Text + `isArray` (the backend splits on the separator).
 */
export function looksLikeArray(sampleValues: string[], separator = ';'): boolean {
  const values = sampleValues.map((v) => v.trim()).filter((v) => v.length > 0);
  if (values.length === 0) return false;
  const withSeparator = values.filter((v) => v.includes(separator)).length;
  return withSeparator > values.length / 2;
}

/** Sampled values for one column (by header index) across the preview rows. */
export function sampleValuesFor(result: ParseHeaderSampleResponse, headerIndex: number): string[] {
  return result.sampleRows.map((row) => row[headerIndex] ?? '');
}

/**
 * Default mapping for a single column, with an inferred field type. A column
 * delimited by the import's multi-value separator is promoted to Long Text +
 * `isArray` so the multi-value guess can surface (the wizard only shows the
 * array switch for Long Text fields).
 */
export function defaultColumn(header: string, sampleValues: string[], multiValue = ';'): ColumnMappingState {
  const isArray = looksLikeArray(sampleValues, multiValue);
  return {
    header,
    fieldKey: generateFieldKey(header),
    displayName: header,
    fieldType: isArray ? 'Long Text' : inferFieldType(sampleValues),
    isArray,
    ...(isArray ? { arraySeparator: multiValue } : {}),
    enabled: true,
    isIdentifier: false,
  };
}

/** Normalise a load file header for cross-import matching: trimmed, case-insensitive. */
function normaliseHeader(header: string): string {
  return header.trim().toLowerCase();
}

export interface CopyMappingsResult {
  columnMapping: ColumnMappingState[];
  matchedCount: number;
  skipped: { header: string; reason: string }[];
}

/**
 * Overlay a previous import's confirmed column mapping onto the current file's
 * columns, matching by header text (case-insensitive). For each header that
 * exists in both, the previous field config (display name, type, array settings)
 * is carried over and the column is enabled. Current-file columns absent from the
 * source keep their inferred defaults; source headers absent from the current file
 * are reported as skipped. The stored source snapshot only contains the columns the
 * user previously imported, so every matched header is enabled.
 */
export function buildCopiedMappings(
  currentColumns: ColumnMappingState[],
  sourceMapping: LoadFileColumnMapping[],
): CopyMappingsResult {
  const currentHeaders = new Set(currentColumns.map((col) => normaliseHeader(col.header)));
  const sourceByHeader = new Map(sourceMapping.map((col) => [normaliseHeader(col.header), col]));

  const columnMapping = currentColumns.map((col) => {
    const source = sourceByHeader.get(normaliseHeader(col.header));
    if (!source) return col;
    const isArray = source.isArray ?? false;
    // Rebuild `arraySeparator` from the source rather than spreading the current
    // column, so a non-array source never leaves a stale separator behind.
    const { arraySeparator: _current, ...rest } = col;
    return {
      ...rest,
      displayName: source.displayName,
      fieldType: source.fieldType,
      isArray,
      ...(isArray && source.arraySeparator !== undefined ? { arraySeparator: source.arraySeparator } : {}),
      enabled: true,
    };
  });

  const skipped = sourceMapping
    .filter((col) => !currentHeaders.has(normaliseHeader(col.header)))
    .map((col) => ({ header: col.header, reason: 'Not found in this file' }));

  const matchedCount = sourceMapping.length - skipped.length;
  return { columnMapping, matchedCount, skipped };
}

/**
 * Map from normalised Wexler document name → its original filename, so the match
 * breakdown can surface the real filename that lined up (not just the key). First
 * writer wins when several files normalise to the same key.
 */
export function buildFilenameKeyMap(names: string[], stripCurrentExtensions: boolean): Map<string, string> {
  const map = new Map<string, string>();
  for (const name of names) {
    const key = normalizeForMatch(name, { stripExtension: stripCurrentExtensions });
    if (key.length > 0 && !map.has(key)) map.set(key, name);
  }
  return map;
}

/**
 * True when a column can't uniquely identify a document: two or more non-empty
 * sample cells are equal after trimming. Computed on raw trimmed values only, so
 * the result is independent of any match toggle and never flips.
 */
export function columnHasDuplicates(sampleValues: string[]): boolean {
  const seen = new Set<string>();
  for (const value of sampleValues) {
    const trimmed = value.trim();
    if (trimmed.length === 0) continue;
    if (seen.has(trimmed)) return true;
    seen.add(trimmed);
  }
  return false;
}

/** Per-value match detail for a single column against the Wexler filename index. */
export interface ColumnMatch {
  /** Number of non-empty sample values. */
  total: number;
  /** How many of those matched a Wexler filename. */
  matched: number;
  /** matched / total as a 0–100 percentage. */
  percent: number;
  /** One entry per sample value, in order. Empty cells have `isEmpty: true`. */
  values: { raw: string; normalized: string; isMatched: boolean; isEmpty: boolean; matchedFilename?: string }[];
  /** Distinct original Wexler filenames that were matched. */
  matchedFilenames: string[];
}

/**
 * Detailed match of a column's sampled values against the Wexler filename index
 * (a normalised key → original name map). Case-sensitive, mirroring the backend;
 * returns the per-value breakdown and the list of matched filenames for the UI.
 */
export function computeColumnMatch(
  sampleValues: string[],
  filenameKeyMap: Map<string, string>,
  options: { stripIncomingExtensions: boolean },
): ColumnMatch {
  const values: ColumnMatch['values'] = [];
  const matchedFilenames = new Set<string>();

  for (const raw of sampleValues) {
    const normalized = normalizeForMatch(raw, { stripExtension: options.stripIncomingExtensions });
    if (normalized.length === 0) {
      values.push({ raw, normalized, isMatched: false, isEmpty: true });
      continue;
    }
    const original = filenameKeyMap.get(normalized);
    const isMatched = original !== undefined;
    if (isMatched) matchedFilenames.add(original);
    values.push({ raw, normalized, isMatched, isEmpty: false, matchedFilename: original });
  }

  // total/matched exclude empty cells so the "X/50" count reflects real values only.
  const total = values.filter((v) => !v.isEmpty).length;
  const matched = values.filter((v) => v.isMatched).length;
  return {
    total,
    matched,
    percent: total === 0 ? 0 : Math.round((matched / total) * 100),
    values,
    matchedFilenames: [...matchedFilenames],
  };
}

/** Tailwind text colour for a match percentage — green / amber / grey by band. */
export function matchPercentColor(percent: number): string {
  if (percent >= 80) return 'text-green-600';
  if (percent >= 40) return 'text-amber-600';
  return 'text-gray-500';
}

export const STATUS_STYLES: Record<LoadFileImportListItem['status'], string> = {
  uploaded: 'bg-gray-50 text-gray-600',
  parsing: 'bg-gray-50 text-gray-600',
  mapping: 'bg-sky-50 text-sky-500',
  queued: 'bg-sky-50 text-sky-500',
  processing: 'bg-sky-50 text-sky-500',
  completed: 'bg-green-50 text-green-500',
  failed: 'bg-red-50 text-red-500',
};

/** Statuses where the import has already run — the wizard jumps straight to progress. */
export const RUNNING_OR_DONE_STATUSES: LoadFileImportListItem['status'][] = [
  'queued',
  'processing',
  'completed',
  'failed',
];
